import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { useMemo, type FC } from "react";
import { Text } from "@design-system/Typography/Text";
import { useQuestionnaireHeaderData, useQuestionnaireStats } from "../../data";
import { useQuestionnaireStatsTranslations } from "./i18n";
import { useCurrentUserLocale } from "@app/shared/utils/currentUserLocale";
import { SupportedLocales } from "@app/shared/utils/locales";
import {
    StatusTagCircular,
    tagStatus,
} from "@design-system/DataDisplay/StatusTagCircular";
import dayjs from "dayjs";
import { Icon } from "@design-system/Icon";

export const GeneralInfo: FC<{ questionnaireId?: string }> = ({
    questionnaireId,
}) => {
    const { t, keys } = useQuestionnaireStatsTranslations();
    const { dueDateString, isOverdue, lastModifiedString } =
        useDateData(questionnaireId);
    const { status } = useStatusData(questionnaireId);

    return (
        <FlexRow gap="7">
            <FlexCol gap="1.5">
                <Text variant="caption-bold">
                    {t(keys.general_info_status)}
                </Text>
                <FlexRow alignItems="center" gap="2">
                    <StatusTagCircular status={status} size="md" />
                    <Text variant="body-sm">
                        {t(keys.general_info_status, {
                            context: status,
                        })}
                    </Text>
                </FlexRow>
            </FlexCol>
            <FlexCol gap="1.5">
                <Text variant="caption-bold">
                    {t(keys.general_info_due_date)}
                </Text>
                <Text
                    variant={isOverdue ? "body-sm-bold" : "body-sm"}
                    color={isOverdue ? "danger" : "primary"}
                    className="flex items-center gap-1"
                >
                    <Icon name="calendar" />
                    {dueDateString}
                </Text>
            </FlexCol>
            <FlexCol gap="1.5">
                <Text variant="caption-bold">
                    {t(keys.general_info_last_modified)}
                </Text>
                <Text variant="body-sm">{lastModifiedString}</Text>
            </FlexCol>
        </FlexRow>
    );
};

function useDateData(questionnaireId?: string) {
    const { questionnaireHeaderData } =
        useQuestionnaireHeaderData(questionnaireId);
    const userLocale = useCurrentUserLocale(SupportedLocales.EN);

    const dueDateString = useMemo(() => {
        if (!questionnaireHeaderData?.deadline) {
            return "-";
        }

        return dayjs(questionnaireHeaderData.deadline).format(
            userLocale === "fr" ? "DD MMMM, YYYY" : "MMM DD, YYYY",
        );
    }, [questionnaireHeaderData?.deadline, userLocale]);

    const isOverdue = useMemo(() => {
        if (!questionnaireHeaderData?.deadline) {
            return false;
        }

        return dayjs().isAfter(questionnaireHeaderData.deadline);
    }, [questionnaireHeaderData?.deadline]);

    const lastModifiedString = useMemo(() => {
        if (!questionnaireHeaderData?.updatedAt) {
            return "-";
        }

        // if it is today, return the hour and minute
        if (dayjs(questionnaireHeaderData.updatedAt).isSame(dayjs(), "day")) {
            return dayjs(questionnaireHeaderData.updatedAt).format("HH:mm");
        }

        return dayjs(questionnaireHeaderData.updatedAt).format(
            userLocale === "fr" ? "DD MMMM, YYYY" : "MMM DD, YYYY",
        );
    }, [questionnaireHeaderData?.updatedAt, userLocale]);

    return { dueDateString, isOverdue, lastModifiedString };
}

function useStatusData(questionnaireId?: string) {
    const { stats } = useQuestionnaireStats(questionnaireId);

    const status = useMemo(() => {
        if (stats?.completionStats?.dataRows[0].validated === 0) {
            return tagStatus.todo;
        }

        const total =
            stats?.completionStats?.chartMetadata?.totalQuestions ?? 0;
        const completed = stats?.completionStats?.dataRows[0].validated ?? 0;

        if (completed < total) {
            return tagStatus.inProgress;
        }

        return tagStatus.validated;
    }, [stats]);

    return { status };
}
